import { Chip } from "@nt/ui/chip";
import { Field, FieldError, FieldHint, FieldLabel } from "@nt/ui/field";
import { buttonVariants, inputVariants } from "@nt/ui/recipes";
import { Building2, Globe, Landmark, Mail } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";

import { ChoiceCards, type ChoiceOption } from "@/components/choice-cards";
import { QueryView } from "@/components/query-view";
import { Section } from "@/components/section";
import { SelectField } from "@/components/tax-form-fields";
import { ApiError, authPost, messageOf } from "@/lib/api";
import { shortDate } from "@/lib/format";
import { useGet, useWrite } from "@/lib/queries";
import { digitsOnly, useResendCooldown } from "@/pages/sign-in";

export interface MethodData {
  method: {
    kind: string;
    currency: string;
    last4: string | null;
    changedAt: string;
    cooldownUntil: string | null;
  } | null;
  options: {
    kinds: Array<{ kind: string; label: string; currencies: string[] }>;
    localFields: Record<string, string[]>;
    fxNote: string;
    cooldownHours: number;
  };
}

const FIELD_LABEL: Record<string, string> = {
  holderName: "Account holder name",
  iban: "IBAN",
  sortCode: "Sort code",
  accountNumber: "Account number",
  ifsc: "IFSC code",
  clabe: "CLABE",
  routingNumber: "Routing number (9 digits)",
  swift: "SWIFT/BIC code",
  email: "Wise account email",
};

/** One line under each payout kind; kinds the API adds later show none. */
const KIND_DESCRIPTION: Record<string, string> = {
  wise_email: "Paid to the Wise account on this email, in USD.",
  us_ach: "Paid in USD to a bank account in the United States.",
  swift: "Paid in USD to a bank account outside the US, by wire.",
  iban: "Paid in your local currency, converted by Wise.",
};

const KIND_ICON: Record<string, ChoiceOption<string>["icon"]> = {
  wise_email: Mail,
  us_ach: Landmark,
  swift: Globe,
  iban: Building2,
};

export function fieldsFor(
  kind: string,
  currency: string,
  local: Record<string, string[]>,
): string[] {
  if (kind === "wise_email") return ["email"];
  if (kind === "us_ach")
    return ["holderName", "routingNumber", "accountNumber"];
  if (kind === "swift") return ["holderName", "swift", "accountNumber"];
  return local[currency] ?? [];
}

/** The payout kinds as `ChoiceCards` options, with a one-line description each. */
export function kindOptions(
  kinds: MethodData["options"]["kinds"],
): ChoiceOption<string>[] {
  return kinds.map((k) => ({
    value: k.kind,
    label: k.label,
    description: KIND_DESCRIPTION[k.kind],
    icon: KIND_ICON[k.kind],
  }));
}

const kindLabel = (data: MethodData, kind: string) =>
  data.options.kinds.find((k) => k.kind === kind)?.label ?? kind;

/** The current method's facts, or a line saying there is none. */
export function MethodSummary({ data }: { data: MethodData }) {
  const m = data.method;
  if (!m) {
    return (
      <p className="text-sm text-muted-foreground">
        You have not added a payout method yet. Add one below so we can pay you.
      </p>
    );
  }
  return (
    <div className="grid gap-4 text-sm">
      <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="grid gap-0.5">
          <dt className="text-compact text-muted-foreground">Method</dt>
          <dd className="font-medium text-foreground">
            {kindLabel(data, m.kind)}
          </dd>
        </div>
        <div className="grid gap-0.5">
          <dt className="text-compact text-muted-foreground">Currency</dt>
          <dd className="font-medium text-foreground">{m.currency}</dd>
        </div>
        <div className="grid gap-0.5">
          <dt className="text-compact text-muted-foreground">Account</dt>
          <dd className="font-mono font-medium text-foreground tabular-nums">
            {m.last4
              ? // A Wise account is stored as its masked email, not the end of a number.
                m.last4.includes("@")
                ? m.last4
                : `ending ${m.last4}`
              : "On file"}
          </dd>
        </div>
        <div className="grid gap-0.5">
          <dt className="text-compact text-muted-foreground">Changed</dt>
          <dd className="font-medium text-foreground">
            {shortDate(m.changedAt)}
          </dd>
        </div>
      </dl>
      {m.cooldownUntil ? (
        <p
          role="status"
          className="rounded-card bg-warning-tint p-3 text-warning"
        >
          Payouts to this method start after {shortDate(m.cooldownUntil)}. After
          any change we wait {data.options.cooldownHours} hours before paying,
          to protect you from account takeover.
        </p>
      ) : null}
    </div>
  );
}

const SAVED_MESSAGE =
  "Saved. We emailed you a confirmation. Payouts to the new method start after the waiting period.";

export function MethodForm({
  data,
  email,
  onCancel,
  onSaved,
}: {
  data: MethodData;
  email: string;
  /** Present when there is a method to keep: shows "Cancel". */
  onCancel?: () => void;
  /** The page closes the form and says so itself. Without it, the form says so. */
  onSaved?: () => void;
}) {
  const save = useWrite<unknown, unknown>("/api/partner/payout-method", [
    "payout-method",
    "overview",
  ]);
  const formRef = useRef<HTMLFormElement>(null);
  const [kind, setKind] = useState(data.options.kinds[0]?.kind ?? "wise_email");
  const kindDef = data.options.kinds.find((k) => k.kind === kind);
  const [currency, setCurrency] = useState(kindDef?.currencies[0] ?? "USD");
  const [values, setValues] = useState<Record<string, string>>({});
  const [otp, setOtp] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cooldown = useResendCooldown();
  const fields = fieldsFor(kind, currency, data.options.localFields);
  const currencies = kindDef?.currencies ?? ["USD"];

  async function sendCode() {
    setError(null);
    setMessage(null);
    if (formRef.current && !formRef.current.reportValidity()) return;
    setSending(true);
    try {
      await authPost("email-otp/send-verification-otp", {
        email,
        type: "sign-in",
      });
      setCodeSent(true);
      setOtp("");
      cooldown.start();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setSending(false);
    }
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    try {
      await save.mutateAsync({
        method: { kind, currency, details: values },
        otp: otp.trim(),
      });
      setOtp("");
      setCodeSent(false);
      setValues({});
      if (onSaved) onSaved();
      else setMessage(SAVED_MESSAGE);
    } catch (err) {
      setError(messageOf(err));
      // A refusal before the step-up leaves the code good to use again; this
      // one failed after it, so the code in the field is dead.
      if (err instanceof ApiError && err.code === "CODE_USED") setOtp("");
    }
  }

  return (
    <Section
      title={data.method ? "Change payout method" : "Add a payout method"}
      description={`We pay through Wise. We email you a code before saving, and wait ${data.options.cooldownHours} hours before paying a new method.`}
      actions={
        onCancel ? (
          <button
            type="button"
            className={buttonVariants({ variant: "ghost", size: "sm" })}
            onClick={onCancel}
          >
            Cancel
          </button>
        ) : undefined
      }
    >
      <form
        ref={formRef}
        className="grid gap-6"
        onSubmit={(e) => void submit(e)}
      >
        <Field>
          <FieldLabel id="kind-label">How should we pay you?</FieldLabel>
          <ChoiceCards
            name="kind"
            aria-label="How should we pay you?"
            columns={2}
            value={kind}
            options={kindOptions(data.options.kinds)}
            onChange={(next) => {
              const def = data.options.kinds.find((k) => k.kind === next);
              setKind(next);
              setCurrency(def?.currencies[0] ?? "USD");
              setValues({});
            }}
          />
        </Field>
        {currencies.length > 1 ? (
          <SelectField
            id="currency"
            label="Currency"
            value={currency}
            onChange={(next) => {
              setCurrency(next);
              setValues({});
            }}
            hint={data.options.fxNote}
          >
            {currencies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </SelectField>
        ) : null}
        {fields.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <Field
                key={f}
                className={
                  f === "holderName" || f === "email"
                    ? "sm:col-span-2"
                    : undefined
                }
              >
                <FieldLabel htmlFor={`f-${f}`}>
                  {FIELD_LABEL[f] ?? f}
                </FieldLabel>
                <input
                  id={`f-${f}`}
                  required
                  type={f === "email" ? "email" : "text"}
                  autoComplete="off"
                  value={values[f] ?? ""}
                  onChange={(e) =>
                    setValues({ ...values, [f]: e.target.value })
                  }
                  className={inputVariants({
                    mono: f !== "holderName" && f !== "email",
                  })}
                />
              </Field>
            ))}
          </div>
        ) : null}
        <div className="grid gap-3 rounded-card bg-muted p-4">
          <div className="grid gap-0.5">
            <p className="text-sm font-medium text-foreground">
              Confirm with an email code
            </p>
            <p className="text-compact text-muted-foreground">
              {codeSent
                ? `We sent a 6-digit code to ${email}. It expires in a few minutes.`
                : `We email a 6-digit code to ${email} to confirm it is you.`}
            </p>
          </div>
          {codeSent ? (
            <>
              <Field>
                <FieldLabel htmlFor="otp">6-digit code</FieldLabel>
                <input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  required
                  value={otp}
                  onChange={(e) => setOtp(digitsOnly(e.target.value))}
                  className={inputVariants({
                    mono: true,
                    className: "max-w-48 tracking-[0.3em]",
                  })}
                />
              </Field>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="submit"
                  disabled={save.isPending || otp.length !== 6}
                  className={buttonVariants({ size: "lg" })}
                >
                  {save.isPending ? "Saving…" : "Save payout method"}
                </button>
                <button
                  type="button"
                  disabled={sending || cooldown.seconds > 0}
                  className={buttonVariants({ variant: "ghost" })}
                  onClick={() => void sendCode()}
                >
                  {cooldown.seconds > 0
                    ? `Send a new code in ${cooldown.seconds} s`
                    : "Send a new code"}
                </button>
              </div>
            </>
          ) : (
            <div>
              <button
                type="button"
                disabled={sending}
                className={buttonVariants({ variant: "secondary" })}
                onClick={() => void sendCode()}
              >
                {sending ? "Sending…" : "Email me a code"}
              </button>
            </div>
          )}
        </div>
        {error ? <FieldError role="alert">{error}</FieldError> : null}
        {message ? (
          <p role="status" className="text-sm text-success">
            {message}
          </p>
        ) : null}
      </form>
    </Section>
  );
}

/**
 * The loaded tab: the summary card, then the form behind "Change payout method". `readOnly` is a deactivated partner,
 * who is still paid what they earned (spec 7.4): the API takes this change from them, so the page says why it is open.
 * A banned partner never gets here: `App` shows the closed notice instead.
 */
export function PayoutMethodView({
  data,
  readOnly,
  email,
  initiallyEditing = false,
}: {
  data: MethodData;
  readOnly: boolean;
  email: string;
  initiallyEditing?: boolean;
}) {
  const [editing, setEditing] = useState(initiallyEditing);
  const [saved, setSaved] = useState(false);
  const showForm = editing || data.method === null;
  return (
    <>
      <Section
        title="Current payout method"
        actions={
          data.method?.cooldownUntil ? (
            <Chip tone="warning">Waiting period</Chip>
          ) : data.method ? (
            <Chip tone="success">Active</Chip>
          ) : (
            <Chip tone="surface">Not added</Chip>
          )
        }
      >
        <div className="grid gap-4">
          <MethodSummary data={data} />
          {saved ? (
            <p role="status" className="text-sm text-success">
              {SAVED_MESSAGE}
            </p>
          ) : null}
          {readOnly ? (
            <p className="text-sm text-muted-foreground">
              Your account is read-only, but you can still change where we send
              what you earned.
            </p>
          ) : null}
          {data.method && !editing ? (
            <div>
              <button
                type="button"
                className={buttonVariants({ variant: "secondary" })}
                onClick={() => {
                  setSaved(false);
                  setEditing(true);
                }}
              >
                Change payout method
              </button>
            </div>
          ) : null}
        </div>
      </Section>
      {showForm ? (
        <MethodForm
          data={data}
          email={email}
          onCancel={data.method ? () => setEditing(false) : undefined}
          onSaved={() => {
            setEditing(false);
            setSaved(true);
          }}
        />
      ) : null}
    </>
  );
}

/** The Settings › Payout method tab. */
export function PayoutMethodTab({
  readOnly,
  email,
}: {
  readOnly: boolean;
  email: string;
}) {
  const query = useGet<MethodData>(
    "payout-method",
    "/api/partner/payout-method",
  );
  return (
    <QueryView query={query} what="your payout method">
      {(data) => (
        <PayoutMethodView data={data} readOnly={readOnly} email={email} />
      )}
    </QueryView>
  );
}
